import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { NgTemplateOutlet } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  TemplateRef,
  computed,
  inject,
  input,
  linkedSignal,
  signal,
} from "@angular/core";
import { MatDialogClose } from "@angular/material/dialog";

import { ApiHelper } from "../../../helpers/apihelper";
import { ModifyErrorDetail, StartLoadMessage } from "../../../helpers/self-service/models/types";
import { SelfServiceCommand } from "../../../helpers/self-service/self-service-command";
import { transformError } from "../../../helpers/self-service/transform-error";
import { SelfserviceAuthenticationBox } from "../selfservice-authentication-box/selfservice-authentication-box";
import { SelfserviceSuccess } from "../selfservice-success/selfservice-success";

@Component({
  selector: "selfservice-wizard",
  templateUrl: "selfservice-wizard.html",
  styleUrl: "selfservice-wizard.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AngularSvgIconModule,
    MatDialogClose,
    NgTemplateOutlet,
    SelfserviceAuthenticationBox,
    SelfserviceSuccess,
    TranslatePipe,
  ],
})
export class SelfserviceWizard {
  private api = inject(ApiHelper);

  steps = input.required<TemplateRef<void>[]>();
  getCommand = input.required<() => { command: SelfServiceCommand; action: string }>();
  bus = input.required<EventEmitter<StartLoadMessage>>();
  canContinue = input<boolean | ((step: number) => boolean)>(true);
  canConfirm = input<boolean>(true);
  initialStep = input<number>(1);
  successMessage = input<string>();

  // ponytail: linkedSignal resets on input change (dialog re-open), which is correct
  private currentStep = linkedSignal(() => this.initialStep());

  protected canContinueOnStep = computed(() => {
    const fn = this.canContinue();
    return typeof fn === "function" ? fn(this.currentStep()) : fn;
  });

  protected firstStep = computed(() => this.currentStep() === 1);
  protected lastStep = computed(() => this.currentStep() === this.steps().length);
  protected currentTemplate = computed(() => this.steps()[this.currentStep() - 1]);

  protected errors = signal<ModifyErrorDetail[]>([]);
  protected hasErrors = computed(() => this.errors().length > 0);
  protected loading = signal(false);
  private submitted = signal(false);
  protected showSuccess = computed(() => this.submitted() && !!this.successMessage());

  protected prev() {
    this.errors.set([]);
    this.currentStep.update((s) => s - 1);
  }

  protected next() {
    this.errors.set([]);
    this.currentStep.update((s) => s + 1);
  }

  protected async submit() {
    this.errors.set([]);
    this.loading.set(true);
    try {
      const { command, action } = this.getCommand()();
      const response = await this.api.selfServiceCommand(command);
      this.bus().emit({ processId: response?.pid ?? "", action });
      this.submitted.set(true);
    } catch (err) {
      try {
        this.errors.set(transformError((err as HttpErrorResponse).error.detail));
      } catch {
        console.log("unknown error", err);
      }
    } finally {
      this.loading.set(false);
    }
  }
}
